import { SkeletonConsumer } from '@/features/shared/components/SkeletonLoader'
import { type ProductCardProduct } from '@/features/shop/components/ProductCard/types'
import { getProductDetails } from '@/features/shared/utils/getProductDetails'
import styled from 'styled-components'
import { useIsProductSnapEbtEligible } from '@/features/shared/hooks/useIsProductSnapEbtEligible'
import { Body } from '@shipt/design-system-typography'
import { ProductDetailStars } from '@/features/shop/components/ProductDetailRatingsAndReviews/ProductDetailStars'
import { Stack } from '@shipt/design-system-layouts'
import { getProductCardName } from '@/features/shop/components/ProductCard/Utils'

type Props = {
  product: ProductCardProduct
  isInSponsoredShelf?: boolean
  hideRatings?: boolean
  hideSecondaryDetails?: boolean
}

export const ProductCardName = ({
  product,
  isInSponsoredShelf = false,
  hideRatings = false,
  hideSecondaryDetails,
}: Props) => {
  const { is_snap_ebt_eligible, review_stats } = product

  const {
    average_rating: averageRating,
    rating_distribution: ratingDistribution,
    total_ratings: totalRatings,
  } = review_stats ?? {}
  const productName = getProductCardName(product)
  const { subtext } = getProductDetails(product)
  const isEbtEligible = useIsProductSnapEbtEligible(is_snap_ebt_eligible)

  const displayProductRatings = !hideRatings && !!ratingDistribution && !!averageRating

  const handledTotalReviewCount =
    !!totalRatings &&
    `${totalRatings >= 1000 ? (totalRatings / 1000).toFixed(1) + 'K' : totalRatings}`

  return (
    <NameWrapper>
      <Name aria-hidden data-hide-secondary-details={hideSecondaryDetails}>
        <SkeletonConsumer height={60} width="100%">
          <Body data-cy={`product-card-name${product.oos ? ':oos' : ''}`} size="md">
            {productName}
          </Body>
        </SkeletonConsumer>
      </Name>
      <SkeletonConsumer height={20} width={80}>
        {!hideSecondaryDetails && (
          <Subtext size="md" variant="secondary">
            {subtext}
          </Subtext>
        )}
        {!!isEbtEligible && !hideSecondaryDetails && (
          <Subtext size="md" variant="primary">
            EBT eligible
          </Subtext>
        )}
        {!!displayProductRatings && (
          <ProductReviewsWrapper data-product-reviews-wrapper>
            <ProductDetailStars rating={averageRating} isProductCard />
            <Body size="md" variant="secondary">
              ({handledTotalReviewCount})
            </Body>
          </ProductReviewsWrapper>
        )}
        {!!product.featured && !isInSponsoredShelf && (
          <Body size="sm" variant="secondary">
            Sponsored
          </Body>
        )}
      </SkeletonConsumer>
    </NameWrapper>
  )
}

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const Name = styled.div<{
  'data-hide-secondary-details'?: boolean
}>`
  margin-bottom: 0.125rem;

  p {
    max-height: 5.0625rem;
    color: ${({ theme }) => theme.gray900};
    overflow-wrap: break-word;
    overflow: hidden;
    flex-shrink: 1;
  }

  &[data-hide-secondary-details='true'] p {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`

const Subtext = styled(Body)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
`

const ProductReviewsWrapper = styled(Stack)`
  display: inline-flex;
`
